import React, { useState } from 'react';
import {
  X, ArrowRight, ArrowLeft, CheckCircle,
  LayoutDashboard, Wallet, TrendingUp, PiggyBank,
  Target, BarChart2, Sparkles,
} from 'lucide-react';

const STEPS = [
  {
    icon: <Sparkles size={32} className="text-blue-400" />,
    title: 'Welcome to Finovo! 🎉',
    description: "Finovo is your all-in-one personal finance dashboard. We'll give you a quick tour of what you can do here.",
    color: 'from-blue-900/60 via-indigo-900/40 to-purple-900/30',
    border: 'border-blue-700/40',
    tab: null,
  },
  {
    icon: <LayoutDashboard size={32} className="text-emerald-400" />,
    title: 'Overview',
    description: 'Your financial command center. See your net worth, monthly spending, smart alerts, and upcoming events — all in one place.',
    color: 'from-emerald-900/40 to-teal-900/30',
    border: 'border-emerald-700/40',
    tab: 'overview',
    features: ['Net worth KPI card', 'Smart alerts & warnings', 'Goals progress snapshot', 'Upcoming bills & events'],
  },
  {
    icon: <Wallet size={32} className="text-amber-400" />,
    title: 'Budget',
    description: 'Track your income and spending by category. Set budgets, log expenses, and see your 50/30/20 breakdown.',
    color: 'from-amber-900/40 to-orange-900/30',
    border: 'border-amber-700/40',
    tab: 'budget',
    features: ['Set monthly income', 'Create spending categories', 'Log expenses in real-time', '50/30/20 rule analysis'],
  },
  {
    icon: <TrendingUp size={32} className="text-blue-400" />,
    title: 'Net Worth',
    description: 'Track all your assets (investments, cash, real estate) and liabilities (loans, credit cards). Watch your wealth grow over time.',
    color: 'from-blue-900/40 to-cyan-900/30',
    border: 'border-blue-700/40',
    tab: 'networth',
    features: ['Add bank & investment accounts', 'Track liabilities & debt', 'Net worth history chart', 'Estate planning checklist'],
  },
  {
    icon: <Target size={32} className="text-purple-400" />,
    title: 'Goals',
    description: 'Set and track financial milestones — from buying a home to building an emergency fund. Visualize your progress.',
    color: 'from-purple-900/40 to-violet-900/30',
    border: 'border-purple-700/40',
    tab: 'goals',
    features: ['Create savings goals', 'Set target dates & amounts', 'Housing mortgage calculator', 'Track progress visually'],
  },
  {
    icon: <PiggyBank size={32} className="text-pink-400" />,
    title: 'Retirement Planner',
    description: 'Run Monte Carlo simulations to see your retirement success probability. Adjust your strategy with powerful what-if scenarios.',
    color: 'from-pink-900/40 to-rose-900/30',
    border: 'border-pink-700/40',
    tab: 'retirement',
    features: ['500-simulation Monte Carlo', 'Adjust savings & timeline', 'Social Security estimate', 'Strategy presets'],
  },
  {
    icon: <BarChart2 size={32} className="text-indigo-400" />,
    title: 'Investing & More',
    description: 'Track your investment portfolio, RSU vesting schedule, credit score, family finances, and a financial events calendar.',
    color: 'from-indigo-900/40 to-blue-900/30',
    border: 'border-indigo-700/40',
    tab: 'investing',
    features: ['Portfolio allocation', 'RSU vesting tracker', 'Credit score history', 'Family finance planning'],
  },
  {
    icon: <CheckCircle size={32} className="text-emerald-400" />,
    title: "You're all set! 🚀",
    description: "Now it's time to add your financial data. Start with your accounts and budget to unlock personalized insights and alerts.",
    color: 'from-emerald-900/40 to-teal-900/30',
    border: 'border-emerald-700/40',
    tab: null,
    cta: true,
  },
];

export default function OnboardingTutorial({ onComplete, onSkip, setActiveTab }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setStep(s => s + 1);
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const handleGoToTab = () => {
    if (current.tab && setActiveTab) {
      setActiveTab(current.tab);
      onComplete();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mb-4">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`rounded-full transition-all duration-200 ${
                i === step
                  ? 'w-6 h-2 bg-blue-400'
                  : i < step
                  ? 'w-2 h-2 bg-blue-600'
                  : 'w-2 h-2 bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className={`bg-gradient-to-br ${current.color} border ${current.border} rounded-2xl p-8 relative`}>
          {/* Skip button */}
          <button
            onClick={onSkip}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/10 transition-colors"
            title="Skip tutorial"
          >
            <X size={16} />
          </button>

          {/* Step counter */}
          <div className="text-xs text-slate-500 font-medium mb-4">
            Step {step + 1} of {STEPS.length}
          </div>

          {/* Icon */}
          <div className="mb-5">{current.icon}</div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-slate-100 mb-3">{current.title}</h2>

          {/* Description */}
          <p className="text-slate-300 text-sm leading-relaxed mb-5">{current.description}</p>

          {/* Features list */}
          {current.features && (
            <ul className="space-y-2 mb-6">
              {current.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-slate-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          )}

          {/* CTA buttons for last step */}
          {current.cta && (
            <div className="flex flex-wrap gap-2 mb-5">
              <button
                onClick={() => { onComplete(); if (setActiveTab) setActiveTab('networth'); }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Add Accounts →
              </button>
              <button
                onClick={() => { onComplete(); if (setActiveTab) setActiveTab('budget'); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors"
              >
                Set Up Budget →
              </button>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={handlePrev}
              disabled={isFirst}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                isFirst
                  ? 'text-slate-700 cursor-not-allowed'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <ArrowLeft size={15} /> Back
            </button>

            <div className="flex items-center gap-3">
              {current.tab && !current.cta && (
                <button
                  onClick={handleGoToTab}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Explore →
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors shadow-lg shadow-blue-900/40"
              >
                {isLast ? 'Get Started' : 'Next'}
                {!isLast && <ArrowRight size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Skip link */}
        {!isLast && (
          <p className="text-center text-xs text-slate-600 mt-3">
            <button onClick={onSkip} className="hover:text-slate-400 transition-colors">
              Skip tutorial
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
